import React, { useState, useEffect } from 'react';
import { useRouteData, useSiteData, Head } from 'react-static';
import LazyLoad from 'react-lazyload';
// template components
import ContentHub from '@nmx/template/dist/componentLibrary/modules/ContentHub';
import InPageLeadForm from '@nmx/template/dist/componentLibrary/modules/InPageLeadForm';
import Page from '@nmx/template/dist/componentLibrary/layout/Page';
import Theme from '@nmx/template/dist/componentLibrary/Theme';
// native components
import AdvisorConnectModule from './components/advisorConnectModule';
import BrokerDealerDisclosureSection from './components/brokerDealerDisclosureSection';
import CredentialCalloutGrouping from './components/credentialCalloutGrouping';
import Faq from './components/faq';
import FinancialPlanningSection1 from './components/financialPlanningSection1';
import FinancialPlanningSection2 from './components/financialPlanningSection2';
import HeroSection from './components/heroSection';
import IntroSection from './components/introSection';
import Metadata from './components/metadata';
import ProductCalloutGrouping from './components/productCalloutGrouping';
import QuizCallout from './components/quizCallout';
import RelatedLinksSection from './components/relatedLinksSection';
import SectionVideoPlayer from './components/sectionVideoPlayer';
import SuspectEmailCaptureFormSection from './components/suspectEmailCaptureFormSection';
// images for prefetch
import highResDesktopImage from './components/heroSection/images/investments-hero.webp';
import highResMobileImage from './components/heroSection/images/investments-hero-mobile.webp';
// dislaimer footnotes
import {
  BasedOnClientData,
  RankingNmInvestmentsServices,
} from '../../../utils/disclaimerFootnotes/disclaimerFootnotes';
// other utils
import { getImmediateMatchFeatureFlag } from '../../../utils/getFeatureFlags/getFeatureFlags';

export default () => {
  const { config, dynamicConfig } = useSiteData();
  const { pageProps, contentHubItems } = useRouteData();
  // We don't want to load recaptcha on page load (for performance reasons) so we will load it once the in page lead for is engaged with
  const [loadRecaptcha, setLoadRecaptcha] = useState(false);
  const [immediateMatchFeatureFlag, setImmediateMatchFeatureFlag] = useState(false);

  useEffect(() => {
    getImmediateMatchFeatureFlag(setImmediateMatchFeatureFlag, config);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Page
      {...pageProps} // defined in route.js
      headComponent={Head}
      config={config}
      dynamicConfig={dynamicConfig}
      useReCaptcha={loadRecaptcha}
      disableDisclaimerTopBorder
      disclaimerFootnotes={[
        <>
          <sup>1</sup>&nbsp;An investment in any money market fund is not insured or guaranteed by the Federal Deposit Insurance Corporation or any government agency. Although a money market fund seeks to preserve the value of your investment at &#36;1.00 per share, it is possible to lose money by investing in a money market fund. Yield fluctuates. The Fund&#39;s sponsor has no legal obligation to provide financial support to the Fund, and you should not expect that the sponsor will provide financial support to the Fund at any time.
        </>,
        <>
          <sup>2</sup>&nbsp;Stocks&#47;Equities have greater potential for gains as well as greater potential for loss than bonds&#47;fixed income investments.
        </>,
        <>
          {RankingNmInvestmentsServices(3)}
        </>,
        <>
          {BasedOnClientData(4)}
        </>,
      ]}
      breadcrumbsNavigationConfig={[
        {
          pageName: 'Home',
          linkUrl: '/',
          linkAriaLabel: 'Northwestern Mutual Home',
        },
        {
          pageName: 'Investments',
          linkUrl: '/investments/',
          linkAriaLabel: 'Northwestern Mutual Investments',
        },
      ]}>
      <Head>
        {/* prefetch expensive images */}
        <link rel='preload' href={highResMobileImage} as='image' media={`(max-width: ${Theme.breakpoints.values.medium}px)`} />
        <link rel='preload' href={highResDesktopImage} as='image' media={`(min-width: ${Theme.breakpoints.values.medium + 1}px)`} />
      </Head>

      <Metadata
        image={highResDesktopImage}
        pageDescription={pageProps.pageDescription}
        pageTitle={pageProps.pageTitle}
      />

      <HeroSection />

      <IntroSection />

      <ProductCalloutGrouping />

      <QuizCallout />

      <Faq />

      <SuspectEmailCaptureFormSection
        config={config}
        loadRecaptcha={loadRecaptcha}
        setLoadRecaptcha={setLoadRecaptcha}
      />

      <FinancialPlanningSection1 />

      {/* IN-PAGE LEAD FORM SECTION */}
      <LazyLoad
        offset={300}
        height={300}
        once
        placeholder={
          <section id='in-page-lead'>
            <h2>Get matched with a financial advisor in four easy steps.</h2>
          </section>
        }>
        <InPageLeadForm
          sectionId='in-page-lead' // note: id in-page-lead-form will be on the Form element itself.
          config={config}
          formLocation='investments'
          immediateMatchFeatureFlagEnabled={immediateMatchFeatureFlag}
          onFormEngaged={() => {
            if (!loadRecaptcha) {
              setLoadRecaptcha(true);
            }
          }}
          variant='embedded'
        />
      </LazyLoad>

      <FinancialPlanningSection2 />

      <SectionVideoPlayer />

      <CredentialCalloutGrouping />

      <ContentHub
        sectionId='content-hub'
        headingText='See what investing can do for you'
        contentHubItems={contentHubItems} // defined in route.js
        config={config}
      />

      <AdvisorConnectModule />

      <BrokerDealerDisclosureSection />

      <RelatedLinksSection />

    </Page>
  );
};
